import { createGame } from '../core/state';
import { loadState, serialize } from '../core/save';
import type { GameState } from '../core/types';

/**
 * Browser persistence. The save lives under a NEW key so the v0.9 save
 * (`henHouseSave`) is never read or overwritten (plan Autopilot.1 #6).
 */

export const SAVE_KEY = 'henhouse:v1:save';
export const PREFS_KEY = 'henhouse:v1:prefs';
export const LEGACY_SAVE_KEY = 'henHouseSave';
const PLAYER_KEY = 'henhouse:v1:player';

export interface Prefs {
  theme: string;
  sound: boolean;
  telemetry: boolean;
  coach: boolean;
  legacyNoticeShown: boolean;
  introDone: boolean;
}

const DEFAULT_PREFS: Prefs = { theme: 'twilight', sound: false, telemetry: true, coach: true, legacyNoticeShown: false, introDone: false };

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function loadPrefs(): Prefs {
  try {
    return { ...DEFAULT_PREFS, ...JSON.parse(storage()?.getItem(PREFS_KEY) ?? '{}') };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

export function savePrefs(p: Prefs): void {
  try {
    storage()?.setItem(PREFS_KEY, JSON.stringify(p));
  } catch {
    /* storage full or blocked — prefs are a convenience */
  }
}

export interface LoadResult {
  state: GameState;
  fresh: boolean;
  error: string | null;
}

export function loadGameState(): LoadResult {
  const raw = storage()?.getItem(SAVE_KEY);
  if (raw) {
    try {
      return { state: loadState(raw), fresh: false, error: null };
    } catch (e) {
      // Keep the unreadable save aside instead of destroying it.
      try { storage()?.setItem(`${SAVE_KEY}:corrupt:${Date.now()}`, raw); } catch { /* ignore */ }
      return { state: newGame(), fresh: true, error: e instanceof Error ? e.message : String(e) };
    }
  }
  return { state: newGame(), fresh: true, error: null };
}

export function newGame(): GameState {
  const seed = (crypto.getRandomValues(new Uint32Array(1))[0] ?? Date.now()) >>> 0;
  return createGame({ seed, wall: Date.now() });
}

export function saveGameState(s: GameState): boolean {
  try {
    storage()?.setItem(SAVE_KEY, serialize(s));
    return true;
  } catch {
    return false;
  }
}

export function clearGameState(): void {
  storage()?.removeItem(SAVE_KEY);
}

export function hasLegacySave(): boolean {
  return !!storage()?.getItem(LEGACY_SAVE_KEY);
}

/** Anonymous random player id (never the farm name — v0.9 merged players who picked the same name). */
export function playerId(): string {
  const st = storage();
  let id = st?.getItem(PLAYER_KEY);
  if (!id) {
    id = crypto.randomUUID?.() ?? `p${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
    try { st?.setItem(PLAYER_KEY, id); } catch { /* ignore */ }
  }
  return id;
}
